import { vueEnv } from "@/config/vueEnv";

/** Default: re-login and refresh menu permissions every 24 hours. */
export const SESSION_TTL_MS =
  Number(vueEnv("VUE_APP_SESSION_TTL_HOURS") || 24) * 60 * 60 * 1000;

export const AUTH_USER_KEY = "auth_user";
export const AUTH_TOKEN_KEY = "auth_token";
export const AUTH_AT_KEY = "auth_logged_in_at";
export const AUTH_COOKIE_FLAG_KEY = "auth_session_cookie";
export const AUTH_BEARER_FALLBACK_KEY = "auth_bearer_fallback";
export const MENU_ACCESS_AT_KEY = "menu_access_loaded_at";

/** Production uses HttpOnly cookie — no Bearer token in Network tab when cookie works. */
export function useSessionCookie() {
  const flag = String(vueEnv("VUE_APP_USE_SESSION_COOKIE") || "").trim().toLowerCase();
  if (flag === "false" || flag === "0" || flag === "no") return false;
  if (flag === "true" || flag === "1" || flag === "yes") return true;
  return typeof process !== "undefined" && process.env.NODE_ENV === "production";
}

export function isSessionExpired(atMs = readAuthTimestamp()) {
  if (!atMs) return true;
  return Date.now() - atMs > SESSION_TTL_MS;
}

export function readAuthTimestamp() {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(AUTH_AT_KEY);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function usesBearerFallback() {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(AUTH_BEARER_FALLBACK_KEY) === "1";
}

export function hasActiveSession() {
  if (typeof localStorage === "undefined") return false;
  if (isSessionExpired()) return false;
  if (!localStorage.getItem(AUTH_USER_KEY)) return false;
  if (useSessionCookie()) {
    if (usesBearerFallback()) {
      return !!localStorage.getItem(AUTH_TOKEN_KEY);
    }
    return localStorage.getItem(AUTH_COOKIE_FLAG_KEY) === "1";
  }
  return !!localStorage.getItem(AUTH_TOKEN_KEY);
}

export function loadStoredToken() {
  if (typeof localStorage === "undefined") return null;
  if (isSessionExpired()) {
    clearAuthStorage();
    return null;
  }
  if (useSessionCookie() && !usesBearerFallback()) {
    return null;
  }
  return localStorage.getItem(AUTH_TOKEN_KEY) || null;
}

export function loadStoredUser() {
  if (typeof localStorage === "undefined") return null;
  if (isSessionExpired()) {
    clearAuthStorage();
    return null;
  }
  if (!hasActiveSession()) {
    clearAuthStorage();
    return null;
  }
  try {
    return JSON.parse(localStorage.getItem(AUTH_USER_KEY) || "null");
  } catch {
    clearAuthStorage();
    return null;
  }
}

export function persistAuthSession(user, token, options = {}) {
  if (typeof localStorage === "undefined") return;
  const { cookieOnly = false, bearerFallback = false } = options;
  const at = Date.now();
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  localStorage.setItem(AUTH_AT_KEY, String(at));

  if (!useSessionCookie()) {
    localStorage.removeItem(AUTH_COOKIE_FLAG_KEY);
    localStorage.removeItem(AUTH_BEARER_FALLBACK_KEY);
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    }
    return;
  }

  if (bearerFallback && token) {
    localStorage.setItem(AUTH_BEARER_FALLBACK_KEY, "1");
    localStorage.removeItem(AUTH_COOKIE_FLAG_KEY);
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    return;
  }

  if (cookieOnly) {
    localStorage.setItem(AUTH_COOKIE_FLAG_KEY, "1");
    localStorage.removeItem(AUTH_BEARER_FALLBACK_KEY);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return;
  }

  localStorage.setItem(AUTH_COOKIE_FLAG_KEY, "1");
  localStorage.removeItem(AUTH_BEARER_FALLBACK_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

/** Wipe browser storage and Cache API entries (logout / session end). */
export function clearAppCacheOnLogout() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage?.clear();
  } catch (_) {
    /* ignore */
  }
  try {
    localStorage?.clear();
  } catch (_) {
    /* ignore */
  }
  if (typeof caches !== "undefined" && typeof caches.keys === "function") {
    caches
      .keys()
      .then((names) => Promise.all(names.map((name) => caches.delete(name))))
      .catch(() => {});
  }
}

export function clearAuthStorage() {
  clearAppCacheOnLogout();
}

export function touchMenuAccessLoadedAt() {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(MENU_ACCESS_AT_KEY, String(Date.now()));
}

export function isMenuAccessStale() {
  if (typeof localStorage === "undefined") return true;
  const raw = localStorage.getItem(MENU_ACCESS_AT_KEY);
  const at = Number(raw);
  if (!Number.isFinite(at) || at <= 0) return true;
  return Date.now() - at > SESSION_TTL_MS;
}
